import { useState, useCallback, useEffect, useRef } from 'react';
import { NotificationType, NotificationAction } from '../components/ModernNotification';

export interface NotificationOptions {
  title: string;
  message: string;
  type?: NotificationType;
  duration?: number;
  actions?: NotificationAction[];
}

interface QueuedNotification extends NotificationOptions {
  id: string;
  type: NotificationType;
  duration: number;
  actions: NotificationAction[];
}

export const useModernNotification = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [type, setType] = useState<NotificationType>('info');
  const [duration, setDuration] = useState(5000);
  const [actions, setActions] = useState<NotificationAction[]>([]);
  const [currentId, setCurrentId] = useState<string>('');

  const queueRef = useRef<QueuedNotification[]>([]);
  const isProcessingRef = useRef(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const processQueue = useCallback(() => {
    if (isProcessingRef.current || queueRef.current.length === 0) {
      return;
    }

    isProcessingRef.current = true;
    const notification = queueRef.current.shift()!;

    setCurrentId(notification.id);
    setTitle(notification.title);
    setMessage(notification.message);
    setType(notification.type);
    setDuration(notification.duration);
    setActions(notification.actions);
    setIsOpen(true);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (notification.duration > 0) {
      timeoutRef.current = setTimeout(() => {
        setIsOpen(false);
        isProcessingRef.current = false;

        setTimeout(() => {
          processQueue();
        }, 300);
      }, notification.duration);
    }
  }, []);

  const showNotification = useCallback((options: NotificationOptions) => {
    const notification: QueuedNotification = {
      id: `notif-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      title: options.title,
      message: options.message,
      type: options.type || 'info',
      duration: options.duration || 5000,
      actions: options.actions || []
    };

    queueRef.current.push(notification);

    if (!isProcessingRef.current) {
      processQueue();
    }
  }, [processQueue]);

  const hideNotification = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    setIsOpen(false);
    isProcessingRef.current = false;

    setTimeout(() => {
      processQueue();
    }, 300);
  }, [currentId, processQueue]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    isOpen,
    title,
    message,
    type,
    duration,
    actions,
    showNotification,
    hideNotification
  };
};

export default useModernNotification;